import moment from 'moment-timezone';
import { TimezoneService } from '@shared/services/timezone.service';

/**
 * Sets the time of the given date to the start of the day (00:00:00.000).
 * @param date - The date object.
 * @returns A new date object with time set to the start of the day.
 */
export function setStartOfDay(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}

/**
 * Sets the time of the given date to the end of the day (23:59:59.999).
 * @param date - The date object.
 * @returns A new date object with time set to the end of the day.
 */
export function setEndOfDay(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}

export function is24hRange(start: string, finish: string): boolean {
  if (!start || !finish) return false;
  const startDate = new Date(start);
  const endDate = new Date(finish);
  const diffMs = endDate.getTime() - startDate.getTime();
  return diffMs === 24 * 60 * 60 * 1000;
}

export function isFullDayRange(start: string, finish: string): boolean {
  if (!start || !finish) return false;

  const startDate = new Date(start);
  const endDate = new Date(finish);

  const diffMs = endDate.getTime() - startDate.getTime();
  const oneDayMs = 24 * 60 * 60 * 1000;
  const tolerance = 1; // allow 1 millisecond

  return Math.abs(diffMs - oneDayMs) <= tolerance;
}

export function isToday(dateStr: string): boolean {
  const date = new Date(dateStr);
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

export function isTodayRange(
  start: string | Date,
  finish: string | Date,
  timezoneService: TimezoneService,
): boolean {
  if (!start || !finish || !timezoneService) return false;

  let timezone = timezoneService.getTimezone().replace(/\s*\(GMT[+-]\d+\)$/, '');

  const startMoment = moment.tz(start, timezone);
  const finishMoment = moment.tz(finish, timezone);
  const now = moment().tz(timezone);

  const isSameDayAsToday = startMoment.isSame(now, 'day') && finishMoment.isSame(now, 'day');

  const isStartOfDay = startMoment.isSame(startMoment.clone().startOf('day'));
  const isEndOfDay = finishMoment.isSame(finishMoment.clone().endOf('day'));

  return isSameDayAsToday && isStartOfDay && isEndOfDay;
}
